// Hook: useVehicleData
// Encapsulates hardware selection, data fetch, caching, and derived state
import { useState, useRef, useCallback } from "react";
import { assignMaterialVehicleDetailsList } from "../../../services/C4S Decommission - Replacement/assignMaterialVehicleDetails";

export default function useVehicleData(materialsByKey, savedMaterialKeys) {
  const [vehicleList, setVehicleList] = useState([]);
  const [isVehicleLoading, setIsVehicleLoading] = useState(false);
  const [activeBundleId, setActiveBundleId] = useState("");
  const [combinedVehiclesByBundle, setCombinedVehiclesByBundle] = useState({});
  const [selectedMaterialDetails, setSelectedMaterialDetails] = useState(null);
  const [materialVehiclesCache, setMaterialVehiclesCache] = useState({});
  const inFlightRef = useRef(false);

  const fetchForHardware = useCallback(
    async ({ key, notify }) => {
      // Prevent concurrent fetches regardless of key
      if (inFlightRef.current) {
        return;
      }

      const found = materialsByKey?.[key];
      const primaryBundleId = found?.bundleIds?.[0] || "";
      setActiveBundleId(primaryBundleId);
      setSelectedMaterialDetails(found ? { ...found } : null);

      if (savedMaterialKeys?.[key] && materialVehiclesCache[key]) {
        setCombinedVehiclesByBundle(materialVehiclesCache[key]);
        setVehicleList(materialVehiclesCache[key]);
        return;
      }

      try {
        inFlightRef.current = true;
        setIsVehicleLoading(true);
        // Clear list at the start of a new fetch to avoid stale display
        setVehicleList([]);

        if (!primaryBundleId) {
          setIsVehicleLoading(false);
          inFlightRef.current = false;
          return;
        }

        const payload = {
          hardwareId: found?.hardwareId || "",
          subscriptionId: found?.subscriptionId || "",
          techRefId: found?.techRefId || "",
          workticketId: Array.isArray(found?.workticketIds) ? found.workticketIds.join(",") : "",
          esn: Array.isArray(found?.esns) ? found.esns.join(",") : "",
        };

        const response = await assignMaterialVehicleDetailsList(payload);

        if (response?.status === 200 && response?.data?.data) {
          const rawDataArray = response.data.data;
          const bundleMap = {};
          if (Array.isArray(rawDataArray)) {
            rawDataArray.forEach((entryObj) => {
              if (entryObj && typeof entryObj === "object") {
                Object.keys(entryObj).forEach((bundleIdKey) => {
                  const groups = entryObj[bundleIdKey];
                  if (Array.isArray(groups)) {
                    bundleMap[bundleIdKey] = groups.map((g) => ({
                      address: g.address || {},
                      contact: g.contact || {},
                      vehicles: Array.isArray(g.vehicles) ? g.vehicles : [],
                    }));
                  } else {
                    bundleMap[bundleIdKey] = [];
                  }
                });
              }
            });
          }
          setCombinedVehiclesByBundle(bundleMap);
          setVehicleList(bundleMap);
          setMaterialVehiclesCache((prev) => ({ ...prev, [key]: bundleMap }));
        } else if (response?.status === 200 && !response?.data?.data) {
          setVehicleList([]); // Use empty array consistently for no data
          setCombinedVehiclesByBundle({});
          notify && notify("No data to display", "error");
        } else {
          setVehicleList([]); // Use empty array consistently on error
          setCombinedVehiclesByBundle({});
          notify && notify("Something went wrong, please try again.", "error");
        }
      } catch (e) {
        setVehicleList([]);
        notify && notify("Something went wrong, please try again.", "error");
      } finally {
        setIsVehicleLoading(false);
        inFlightRef.current = false;
      }
    },
    [materialsByKey, savedMaterialKeys, materialVehiclesCache]
  );

  return {
    vehicleList,
    isVehicleLoading,
    activeBundleId,
    combinedVehiclesByBundle,
    selectedMaterialDetails,
    materialVehiclesCache,
    setMaterialVehiclesCache,
    setVehicleList,
    setCombinedVehiclesByBundle,
    setActiveBundleId,
    setSelectedMaterialDetails,
    fetchForHardware,
    setIsVehicleLoading,
  };
}
